import * as XLSX from 'xlsx';
import { dataRefreshed } from './performanceRawExport';
import { partitionDismissed, partitionApplied, cardsForRoute } from './performanceCards';
import { diagnosisCards } from './diagnosisFindings';
import { cardEntityKeys } from './deliveryBlockers';

const SECTIONS = [
  ['Diagnostics', 'diagnostic', 'diagnostics'],
  ['Pacing', 'pacing', 'pacing'],
  ['Optimizations', 'performance', 'recommendations'],
];

// Excel limits text to 32,767 characters. Continuation rows preserve long
// evidence and nested arrays instead of silently truncating the analysis.
function detailRows(value, path = '', prefix = []) {
  if (value && typeof value === 'object' && Object.keys(value).length) {
    return Object.entries(value).flatMap(([key, child]) => detailRows(
      child, Array.isArray(value) ? `${path}[${key}]` : (path ? `${path}.${key}` : key), prefix,
    ));
  }
  const scalar = value == null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : value;
  if (typeof scalar !== 'string') return [[...prefix, path, 1, scalar]];
  const rows = [];
  for (let i = 0; i < Math.max(1, scalar.length); i += 32000) {
    rows.push([...prefix, path, rows.length + 1, scalar.slice(i, i + 32000)]);
  }
  return rows;
}

function addSheet(workbook, name, headers, rows, widths) {
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  sheet['!cols'] = widths.map(wch => ({ wch }));
  sheet['!autofilter'] = { ref: sheet['!ref'] };
  XLSX.utils.book_append_sheet(workbook, sheet, name);
}

function text(value) {
  if (value == null) return '';
  const out = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return out.length > 32000 ? `${out.slice(0, 31900)}… (complete text in Details sheet)` : out;
}

/** All three analyses, regardless of the active tab. The hook's runs are
 * already scoped to the selected campaigns / saved report. No new requests. */
export function buildPerformanceWorkbook({ runs, dismissals, accountId, campaignId = null, campaignIds = [], now = new Date() }) {
  const workbook = XLSX.utils.book_new();
  const sourceRuns = runs.runs || runs.data?.runs || [];
  const refreshed = dataRefreshed(sourceRuns);
  const dismissalRecords = dismissals?.dismissals || [];
  const cards = runs.cards || [];
  const diagnosticCards = cardsForRoute(cards, 'diagnostics');
  const liveDiagnostic = partitionDismissed(diagnosticCards, dismissalRecords).live;
  const findings = [...cards, ...diagnosisCards(
    runs.byKind?.diagnostic?.diagnoses || [],
    cardEntityKeys(liveDiagnostic),
    liveDiagnostic,
  )];

  addSheet(workbook, 'Summary', ['Field', 'Value'], [
    ['Account ID', accountId], ['Exported at (UTC)', now.toISOString()],
    ['Data refreshed (newest analysis, UTC)', refreshed?.newestCompletedAt || 'No completed analysis'],
    ['Oldest analysis (UTC)', refreshed?.oldestCompletedAt || ''],
    ['Analyses older than the newest by over a day', refreshed ? refreshed.olderThanNewestByADay : ''],
    ['Campaign scope', campaignId || (campaignIds.length ? campaignIds.join(', ') : 'All campaigns in the current view')],
    ['Contents', 'Latest loaded diagnostics, pacing and optimizations, including dismissed/applied findings and complete run details.'],
    ['Reading this workbook', 'Finding sheets are readable summaries. Details sheets preserve every run field; Part identifies continuation rows for long text.'],
    ['Runs exported', sourceRuns.length],
    ['Runs error', runs.error || ''], ['Dismissals error', dismissals?.error || ''],
    ...SECTIONS.map(([label, kind]) => [label, sourceRuns.some(run => run.analysisKind === kind) ? 'Available' : 'No completed analysis in this scope']),
  ], [28, 100]);

  addSheet(workbook, 'Runs', ['Campaign', 'Campaign ID', 'Analysis', 'Completed at', 'Run ID', 'Platform'],
    sourceRuns.map(run => [run.campaignName, run.campaignId, run.analysisKind, run.completedAt, run.messageId, run.destination]),
    [40, 38, 20, 28, 38, 24]);

  for (const [label, kind, route] of SECTIONS) {
    const selected = cardsForRoute(findings, route);
    const rows = selected.map(card => {
      const dismissed = partitionDismissed([card], dismissalRecords).dismissed[0];
      const applied = partitionApplied([card]).applied.length > 0;
      return [
        card.campName, card.camp, card.head || card.plain, card.plain,
        dismissed ? 'Dismissed' : applied ? (card.appliedEdit?.status || 'Applied') : 'Open',
        card.priBand, card.pri, card.conf, card.tn, card.tid, card.action,
        card.cur, card.rec, card.ev, card.me, card.coi, card.sar, card.ccy,
        dismissed?.dismissal?.reason, card.messageId,
      ].map(value => typeof value === 'number' ? value : text(value));
    });
    addSheet(workbook, label, [
      'Campaign', 'Campaign ID', 'Finding', 'Summary', 'Status', 'Priority',
      'Priority score', 'Confidence', 'Target', 'Target ID', 'Action',
      'Current value', 'Recommended value', 'Evidence', 'Reasoning',
      'Cost of inaction', 'Spend at risk', 'Currency', 'Dismissal reason', 'Run ID',
    ], rows, [36, 38, 65, 85, 16, 14, 16, 14, 35, 30, 28, 40, 60, 85, 85, 65, 18, 12, 45, 38]);

    // Includes diagnoses, pacing math, experiments, Google reviews, judge
    // verdicts, suppressed/hidden suggestions, and any future result fields.
    addSheet(workbook, `${label} Details`, ['Campaign', 'Campaign ID', 'Run ID', 'Field', 'Part', 'Value'],
      sourceRuns.filter(run => run.analysisKind === kind).flatMap(run => detailRows(
        run, '', [run.campaignName || '', run.campaignId || '', run.messageId || ''],
      )), [38, 38, 38, 85, 8, 100]);
  }

  addSheet(workbook, 'Dismissals', ['Field', 'Part', 'Value'], detailRows(dismissals?.data || { dismissals: dismissalRecords }), [85, 8, 100]);
  return workbook;
}

export function downloadPerformanceExcel(options) {
  const now = new Date();
  const refreshed = dataRefreshed(options.runs?.runs || options.runs?.data?.runs || []);
  const data = refreshed ? `-data-${refreshed.newestCompletedAt.slice(0, 10)}` : '';
  XLSX.writeFile(buildPerformanceWorkbook({ ...options, now }), `performance-analysis${data}-exported-${now.toISOString().slice(0, 10)}.xlsx`, { compression: true });
}
